import { useTranslation } from 'react-i18next';

import List from '@pelckmans/elementis/components/list';
import ListGroup from '@pelckmans/elementis/components/list-group';
import Section from '@pelckmans/elementis/components/section';
import Toggle from '@pelckmans/elementis/components/toggle';

import { ModalContextProvider } from '../../../contexts/ModalContext';
import useGroups, { SchoolYear } from '../hooks/useGroups';
import AddGroupButton from './AddGroupButton';
import GroupsEmptyState from './GroupsEmptyState';
import LoadingState from '../../../shared-components/LoadingState';
import { Group } from '../../../api/groups';
import useStudioGroup from '../../../hooks/useStudioGroup';
import GroupListItem from './GroupListItem';
import GroupListItemVO from './GroupListItem.vo';
import AddGroupButtonVO from './AddGroupButton.vo';
import { useState } from 'react';

const renderSchoolNameWithCities = (school: Group['school']) => {
  if (school.cities) {
    return `${school.name}, ${school.cities}`;
  }

  return school.name;
};

type Props = {
  schoolYear: SchoolYear;
};

function OverviewMyGroups({ schoolYear }: Props) {
  const studioGroup = useStudioGroup();
  const { groupsWithSchool, isLoadingGroups, onGroupsChange } = useGroups();
  const { t } = useTranslation();
  const [showHiddenGroups, setShowHiddenGroups] = useState(false);
  const isVo = studioGroup === 'VO';

  const hasPassed = schoolYear.hasPassed;
  const isCurrent = schoolYear.isCurrent;

  const AddGroupAction = () => {
    if (isLoadingGroups || hasPassed || groupsWithSchool.length === 0) return undefined;

    return !isVo ? (
      <AddGroupButton onGroupsChange={onGroupsChange} schoolYear={schoolYear} size="sm" />
    ) : (
      <AddGroupButtonVO />
    );
  };

  const showToggle =
    isVo && isCurrent && groupsWithSchool.some(({ groups }) => groups.some(group => group.status === 'closed'));

  const filteredGroupsWithSchool = showToggle
    ? groupsWithSchool.map(({ school, groups }) => ({
        school,
        groups: showHiddenGroups ? groups : groups.filter(group => group.status !== 'closed'),
      }))
    : groupsWithSchool;

  const visibleGroups = filteredGroupsWithSchool.flatMap(gws => gws.groups);

  return (
    <Section
      title={t('PAGES.MY_GROUPS.SECTION_TITLE', {
        schoolYearStartYear: schoolYear.startYear,
        schoolYearEndYear: schoolYear.endYear,
      })}
      actions={AddGroupAction()}
      actionGroupRole={undefined}
      actionGroupAriaLabel={undefined}
      toggle={
        showToggle ? (
          <Toggle
            disabled={hasPassed}
            checked={showHiddenGroups}
            label={t('PAGES.MY_GROUPS.TOGGLE_HIDDEN_GROUPS.LABEL')}
            onChange={() => setShowHiddenGroups(!showHiddenGroups)}
          />
        ) : undefined
      }
    >
      <div id="my-groups-overview">
        {isLoadingGroups && (
          <LoadingState variant="list" aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.LOADING.A11Y_LABEL')} />
        )}

        {!isLoadingGroups && (
          <>
            {visibleGroups.length === 0 ? (
              <GroupsEmptyState schoolYear={schoolYear} />
            ) : (
              <ListGroup>
                {filteredGroupsWithSchool.map(({ school, groups }) => {
                  return (
                    <List key={school.id}>
                      <List.Header title={renderSchoolNameWithCities(school)} prominent titleTag="h3" />
                      {isVo && groups.map(group => <GroupListItemVO key={group.id} group={group} />)}
                      {!isVo && groups.map(group => <GroupListItem key={group.id} group={group} />)}
                    </List>
                  );
                })}
              </ListGroup>
            )}
          </>
        )}
      </div>
    </Section>
  );
}

export default function OverviewMyGroupsWrapper(props: Props) {
  return (
    <ModalContextProvider>
      <OverviewMyGroups {...props} />
    </ModalContextProvider>
  );
}
